<template>
  <div class="page">
    <el-card>
      <div slot="header" class="clearfix">
        <span>订单管理</span>
      </div>

      <!-- 订单表格 -->
      <el-table
        :data="orders"
        border
        stripe
        style="width: 100%"
        v-loading="loading"
      >
        <el-table-column prop="id" label="订单ID" width="80" />

        <el-table-column label="联系人" width="120">
          <template slot-scope="{ row }">
            {{ row.name }}
          </template>
        </el-table-column>

        <el-table-column prop="contact" label="电话" width="130" />

        <el-table-column
          prop="addr"
          label="地址"
          min-width="220"
          show-overflow-tooltip
        />

        <el-table-column prop="metal_type" label="金属类型" width="100" />
        <el-table-column prop="weight" label="预约重量(kg)" width="120" />
        <el-table-column
          prop="actual_weight"
          label="实际重量(kg)"
          width="120"
        />

        <el-table-column prop="price" label="单价(元/kg)" width="120" />
        <el-table-column prop="amount" label="金额(元)" width="120" />

        <el-table-column
          prop="appointment_time_text"
          label="预约时间"
          width="180"
        />

        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row.status)">
              {{ statusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <!-- 预约图片略缩图：用第一张，预览全部 -->
        <el-table-column label="图片" width="120">
          <template slot-scope="{ row }">
            <el-image
              v-if="imageList(row.image_url).length"
              :src="firstImage(row.image_url)"
              style="width: 60px; height: 60px; border-radius: 4px;"
              fit="cover"
              :preview-src-list="imageList(row.image_url)"
            />
          </template>
        </el-table-column>

        <!-- 备注 -->
        <el-table-column
          prop="remark"
          label="备注"
          min-width="150"
          show-overflow-tooltip
        />

        <!-- 操作：审核 + 详情 -->
        <el-table-column label="操作" width="260" fixed="right">
          <template slot-scope="{ row }">
            <!-- 只有待审核才可以操作 -->
            <el-button
              v-if="row.status === 'waiting_audit'"
              type="success"
              size="mini"
              @click="audit(row, 'approve')"
            >
              通过
            </el-button>
            <el-button
              v-if="row.status === 'waiting_audit'"
              type="danger"
              size="mini"
              @click="audit(row, 'reject')"
            >
              拒绝
            </el-button>

            <el-button size="mini" type="primary" plain @click="openDetail(row)">
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页（后端分页） -->
      <div class="pagination-box">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :page-size="pageSize"
          :page-sizes="[5, 10, 20, 50]"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 右侧详情抽屉 -->
    <el-drawer
      title="订单详情"
      :visible.sync="detailVisible"
      size="520px"
      direction="rtl"
    >
      <el-descriptions :column="1" border>
        <el-descriptions-item label="订单ID">
          {{ detail.id }}
        </el-descriptions-item>
        <el-descriptions-item label="用户ID">
          {{ detail.user_id }}
        </el-descriptions-item>
        <el-descriptions-item label="联系人">
          {{ detail.name }}
        </el-descriptions-item>
        <el-descriptions-item label="电话">
          {{ detail.contact }}
        </el-descriptions-item>
        <el-descriptions-item label="地址">
          {{ detail.addr }}
        </el-descriptions-item>
        <el-descriptions-item label="省份">
          {{ detail.province }}
        </el-descriptions-item>
        <el-descriptions-item label="城市">
          {{ detail.city }}
        </el-descriptions-item>
        <el-descriptions-item label="区县">
          {{ detail.district }}
        </el-descriptions-item>
        <el-descriptions-item label="经度">
          {{ detail.lng }}
        </el-descriptions-item>
        <el-descriptions-item label="纬度">
          {{ detail.lat }}
        </el-descriptions-item>

        <el-descriptions-item label="金属类型">
          {{ detail.metal_type }}
        </el-descriptions-item>
        <el-descriptions-item label="预约重量">
          {{ detail.weight }} kg
        </el-descriptions-item>
        <el-descriptions-item label="实际重量">
          {{ detail.actual_weight }} kg
        </el-descriptions-item>
        <el-descriptions-item label="单价">
          {{ detail.price }}
        </el-descriptions-item>
        <el-descriptions-item label="金额">
          {{ detail.amount }}
        </el-descriptions-item>

        <el-descriptions-item label="预约时间">
          {{ detail.appointment_time_text }}
        </el-descriptions-item>
        <el-descriptions-item label="创建时间">
          {{ detail.create_time_text }}
        </el-descriptions-item>
        <el-descriptions-item label="审核时间">
          {{ detail.audit_time_text }}
        </el-descriptions-item>

        <el-descriptions-item label="状态">
          {{ statusText(detail.status) }}
        </el-descriptions-item>

        <el-descriptions-item label="备注">
          {{ detail.remark }}
        </el-descriptions-item>

        <el-descriptions-item label="回收员ID">
          {{ detail.collector_id }}
        </el-descriptions-item>
        <el-descriptions-item label="回收员备注">
          {{ detail.collector_remark }}
        </el-descriptions-item>

        <!-- 预约图片（多张） -->
        <el-descriptions-item label="预约图片">
          <div v-if="imageList(detail.image_url).length">
            <el-image
              v-for="(url, idx) in imageList(detail.image_url)"
              :key="idx"
              :src="url"
              style="width: 120px; height: 120px; border-radius: 4px; margin-right: 8px;"
              fit="cover"
              :preview-src-list="imageList(detail.image_url)"
            />
          </div>
        </el-descriptions-item>

        <!-- 现场照片（多张） -->
        <el-descriptions-item label="现场照片">
          <div v-if="imageList(detail.photo_url).length">
            <el-image
              v-for="(url, idx) in imageList(detail.photo_url)"
              :key="idx"
              :src="url"
              style="width: 120px; height: 120px; border-radius: 4px; margin-right: 8px;"
              fit="cover"
              :preview-src-list="imageList(detail.photo_url)"
            />
          </div>
        </el-descriptions-item>
      </el-descriptions>

      <!-- 抽屉内也放一组审核按钮（可选） -->
      <div
        style="margin-top:16px; text-align:right"
        v-if="detail.status === 'waiting_audit'"
      >
        <el-button type="success" @click="audit(detail, 'approve')">通过</el-button>
        <el-button type="danger" @click="audit(detail, 'reject')">拒绝</el-button>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Order',
  data() {
    return {
      orders: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      detailVisible: false,
      detail: {}
    }
  },
  mounted() {
    this.loadOrders()
  },
  methods: {
    // 把字符串或数组统一转成数组
    imageList(val) {
      if (Array.isArray(val)) return val
      if (!val) return []
      return [val]
    },
    // 取第一张图片
    firstImage(val) {
      const list = this.imageList(val)
      return list[0] || ''
    },

    // 请求订单列表（当前写的是查全部；只查待审核就把 status 改为 waiting_audit）
    async loadOrders() {
      this.loading = true
      try {
        const res = await axios.get('https://wx.hzxcet.com/api/order/list', {
          params: {
            status: '', // 查全部；只查待审核就改成 'waiting_audit'
            page: this.currentPage,
            page_size: this.pageSize
          }
        })
        const { code, msg, data, total, page, page_size } = res.data || {}
        if (code === 0) {
          this.orders = data || []
          this.total = total || 0
          this.currentPage = page || this.currentPage
          this.pageSize = page_size || this.pageSize
        } else {
          this.$message.error(msg || '获取订单列表失败')
        }
      } catch (err) {
        console.error(err)
        this.$message.error('订单列表接口请求失败')
      } finally {
        this.loading = false
      }
    },

    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.loadOrders()
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.loadOrders()
    },

    statusText(status) {
      return (
        {
          pending: '待处理',
          waiting_audit: '待审核',
          approved: '已通过',
          rejected: '已拒绝'
        }[status] || status
      )
    },
    statusTagType(status) {
      return (
        {
          pending: 'info',
          waiting_audit: 'warning',
          approved: 'success',
          rejected: 'danger'
        }[status] || 'info'
      )
    },

    // 打开详情
    openDetail(row) {
      this.detail = { ...row }
      this.detailVisible = true
    },

    // 审核订单：通过 / 拒绝
    async audit(row, action) {
      const text =
        action === 'approve' ? '通过该订单，并完成结算' : '将订单退回到待处理状态'
      try {
        await this.$confirm(`确认要${text}吗？`, '提示', { type: 'warning' })
      } catch (e) {
        // 用户点了取消
        return
      }

      this.loading = true
      try {
        const res = await axios.post('https://wx.hzxcet.com/api/order/audit', {
          id: row.id,
          action // "approve" or "reject"
        })

        if (res.data.code === 0) {
          this.$message.success(res.data.msg || '操作成功')
          this.loadOrders()
          this.detailVisible = false
        } else {
          this.$message.error(res.data.msg || '操作失败')
        }
      } catch (err) {
        console.error(err)
        this.$message.error('审核接口请求失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.page {
  padding: 10px;
}
.pagination-box {
  margin-top: 20px;
  text-align: right;
}
</style>
